import { css } from 'aphrodite';
import moment from 'moment';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { ICustomSurvey } from '../../../../../extViewmodels';
import { CustomDateRange } from '../../../../components/CustomDateRange';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/Popover';
import { RadioButton } from '../../../../components/RadioButton';
import { CalendarDateIcon } from '../../../../components/svgs/icons/CalendarDateIcon';
import { brandPrimaryHover } from '../../../../styles/colors';
import { styleSheet } from '../styles';

export const DateRangeSettings = ({ survey }: { survey: ICustomSurvey }) => {
	const isArchived = !!survey?.archivedDate;
	const [dateRangeModalIsOpen, setDateRangeModalIsOpen] = React.useState<boolean>(false);

	const { control, reset, watch } = useFormContext<ICustomSurvey>();
	const formValues = watch();
	const hasDateRange = !!formValues.startDate && !!formValues?.expirationDate;

	const handleCloseDateRangeModal = () => {
		setDateRangeModalIsOpen(false);
	};

	const onEditDateRangeClicked = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
		setDateRangeModalIsOpen(true);
		e.stopPropagation();
		e.preventDefault();
	}, []);

	return (
		<section className={css(styleSheet.tabsControllerWrap)}>
			<h3>Survey Date Range</h3>
			<div className={css(styleSheet.formControl)}>
				<Controller
					name='hasDateRange'
					control={control}
					render={({ field: { ref, ...fieldProps } }) => (
						<div className={css(styleSheet.radioButtons)}>
							<RadioButton
								checked={!hasDateRange}
								disabled={isArchived}
								id={'specific-date-range-1'}
								onChange={() => {
									fieldProps.onChange(false);
									reset(values => ({
										...values,
										expirationDate: undefined,
										startDate: new Date().toISOString(),
									}));
								}}
							>
								<span>Survey is available indefinitely into the future</span>
							</RadioButton>

							<Popover open={dateRangeModalIsOpen} onOpenChange={handleCloseDateRangeModal}>
								<PopoverTrigger>
									<RadioButton
										checked={hasDateRange}
										disabled={isArchived}
										id={'specific-date-range'}
										onChange={() => {
											fieldProps.onChange(true);
											setDateRangeModalIsOpen(true);
										}}
									>
										<span>Survey is only available for a specific date range &nbsp;</span>
									</RadioButton>
								</PopoverTrigger>
								<PopoverContent side='right'>
									<CustomDateRange
										minDate={new Date()}
										from={formValues.startDate ? new Date(formValues.startDate) : undefined}
										to={formValues.expirationDate ? new Date(formValues.expirationDate) : undefined}
										resetDates={() => {
											reset(values => ({
												...values,
												expirationDate: undefined,
												startDate: new Date().toISOString(),
											}));
										}}
										endDateTextLabel='End Date'
										endDateRequired={true}
										onChange={(from, to) => {
											reset(values => {
												const fromDateStr = moment(from).toISOString();
												return {
													...values,
													startDate: fromDateStr,
													expirationDate: moment(to).isValid() ? moment(to).endOf('day').toISOString() : undefined,
												};
											});
										}}
									/>
								</PopoverContent>
							</Popover>

							{hasDateRange && (
								<button
									className={css(styleSheet.calendarDateRangeButton)}
									disabled={isArchived}
									onClick={onEditDateRangeClicked}
								>
									<CalendarDateIcon fillColor={brandPrimaryHover} />{' '}
									<span>
										{`${moment(formValues.startDate).format('MM/DD/yyyy')} - ${moment(formValues.expirationDate).format(
											'MM/DD/yyyy'
										)}`}
									</span>
								</button>
							)}
						</div>
					)}
				/>
			</div>
		</section>
	);
};
